// -------------------------------
// TRAINING TYPE INTERFACES
// -------------------------------
export interface PendingTrainingType {
    name?: string;
    description?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteTrainingType {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE TRAINING TYPE — START
// -------------------------------
export const startCreateTrainingTypeFlow = (
    setPendingTrainingType: (data: PendingTrainingType | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingTrainingType({ name: nameFromIntent });

        pushAIChat(`Any description for training type "${nameFromIntent}"?`);
        speak(`Any description for ${nameFromIntent}?`);
        return;
    }

    setPendingTrainingType({});
    pushAIChat("Sure! What should the training type be called?");
    speak("What should the training type be called?");
};

// -------------------------------
// CREATE TRAINING TYPE — PROCESS
// -------------------------------
export const processPendingTrainingType = (
    text: string,
    pending: PendingTrainingType,
    setPendingTrainingType: (data: PendingTrainingType | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Name
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid training type name.");
            speak("Please provide a valid training type name.");
            return;
        }

        setPendingTrainingType({ ...pending, name: cleanName });

        pushAIChat("Any description for this training type?");
        speak("Any description?");
        return;
    }

    // STEP 2 — Description
    if (!pending.description) {
        setPendingTrainingType({ ...pending, description: text });

        pushAIChat("Should this training type be active or inactive?");
        speak("Should this training type be active or inactive?");
        return;
    }

    // STEP 3 — Status (FINAL)
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive")
            ? "inactive"
            : "active";

        pushAIChat(`Creating training type "${pending.name}"...`);
        speak(`Creating training type ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-training-type", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPendingTrainingType(null);
    }
};

// -------------------------------
// DELETE TRAINING TYPE — START
// -------------------------------
export const startDeleteTrainingTypeFlow = (
    setPendingDelete: (data: PendingDeleteTrainingType | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting training type "${nameFromIntent}"...`);
        speak(`Deleting training type ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-training-type", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which training type would you like to delete?");
    speak("Which training type would you like to delete?");
};

// -------------------------------
// DELETE TRAINING TYPE — PROCESS
// -------------------------------
export const processPendingDeleteTrainingType = (
    text: string,
    pending: PendingDeleteTrainingType,
    setPendingDelete: (data: PendingDeleteTrainingType | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid training type name.");
            speak("Please provide a valid training type name.");
            return;
        }

        pushAIChat(`Deleting training type "${cleanName}"...`);
        speak(`Deleting training type ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-training-type", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
    }
};
